/* eslint-disable no-nested-ternary */
import currency from "currency.js";
import { OrderTypeResponse, SideResponse } from "../../../codegen-api";
import { TEXT_COLORS } from "../../../constants/design/colors";
import { SPACING } from "../../../constants/design/spacing";
import { getAssetShortName } from "../../../utils/asset/assets";
import { sideColors } from "../../../utils/sideColors";
import { Spinner } from "../../shared/Spinner";
import {
  ClosePositionButton,
  PlaceOrderButton,
  SubmitWrapper,
  TradeExecutedBar,
} from "../style";
import {
  ActionBtnsRow,
  DepositBtn,
  OutlineActionBtn,
  TradeInfoBottomLabel,
  TradeInfoBottomRow,
  TradeInfoBottomSection,
  TradeInfoBottomValue,
} from "./style";
import { PerpsTradeFormCtx } from "./PerpsTradeForm.ctx";
import { formatFeeRates } from "./perpFees";

export default function PerpsTradeFormSubmitSection({
  ctx,
}: {
  ctx: PerpsTradeFormCtx;
}) {
  const {
    replacePlaceOrderButton,
    isSpot,
    amount,
    orderType,
    price,
    notEnoughBalanceError,
    balanceUnknownReason,
    isLoading,
    isSpotLoading,
    errors,
    customError,
    accountData,
    reduceOnlyNotAllowed,
    reduceOnlyPositionsUnknown,
    reduceOnly,
    orderDirection,
    perpInstrument,
    commonFormTranslations,
    isPerp,
    liquidationPriceToShow,
    contractPriceStep,
    orderNotional,
    requiredMarginWithFee,
    feeStructure,
    openDepositFlow,
    setShowWithdrawModal,
    currentPosition,
    replaceClosePositionButton,
    setClosePositionModal,
    animControls,
  } = ctx;

  return (
    <SubmitWrapper isSticky={false}>
      {replacePlaceOrderButton || (
        <PlaceOrderButton
          data-trade-submit
          disabled={
            isSpot
              ? !amount ||
                Number(amount) <= 0 ||
                (orderType === OrderTypeResponse.Limit &&
                  (!price || Number(price) <= 0)) ||
                notEnoughBalanceError ||
                // Balance not known: neither insufficient nor placeable.
                !!balanceUnknownReason ||
                isLoading ||
                isSpotLoading
              : Object.values(errors).length > 0 ||
                !!balanceUnknownReason ||
                !!customError ||
                accountData?.in_liquidation ||
                isLoading ||
                // Reduce-only must reduce: block when there's no position
                // or the side matches the position (would increase it).
                // Applies to LIMIT too so reduce-only limit stays safe.
                (reduceOnlyNotAllowed && reduceOnly) ||
                // ...and cannot be checked before positions load.
                (reduceOnlyPositionsUnknown && reduceOnly)
          }
          type="submit"
          side={orderDirection}
        >
          {!(isLoading || isSpotLoading) ? (
            isSpot ? (
              notEnoughBalanceError ? (
                "Insufficient balance"
              ) : (
                `${orderDirection === SideResponse.Buy ? "Buy" : "Sell"} ${
                  getAssetShortName(perpInstrument?.underlying_asset) ||
                  perpInstrument?.underlying_asset ||
                  ""
                }`
              )
            ) : (
              commonFormTranslations("place_order", {
                side: commonFormTranslations(orderDirection.toLowerCase()),
              })
            )
          ) : (
            <Spinner color={sideColors(orderDirection).on} />
          )}
        </PlaceOrderButton>
      )}

      {/* ── Trade info: Liquidation Price, Order Value, Margin Required, Fees ── */}
      {isPerp && (
        <TradeInfoBottomSection>
          <TradeInfoBottomRow>
            <TradeInfoBottomLabel $underline>
              Liquidation Price
            </TradeInfoBottomLabel>
            <TradeInfoBottomValue>
              {liquidationPriceToShow
                ? currency(liquidationPriceToShow, {
                    precision: contractPriceStep.price_precision,
                  }).format()
                : "-"}
            </TradeInfoBottomValue>
          </TradeInfoBottomRow>
          <TradeInfoBottomRow>
            <TradeInfoBottomLabel>
              {commonFormTranslations("order_value")}
            </TradeInfoBottomLabel>
            <TradeInfoBottomValue>
              {orderNotional
                ? currency(orderNotional, {
                    precision: contractPriceStep.price_precision,
                  }).format()
                : "N/A"}
            </TradeInfoBottomValue>
          </TradeInfoBottomRow>
          <TradeInfoBottomRow>
            <TradeInfoBottomLabel>
              {commonFormTranslations("required_margin")}
            </TradeInfoBottomLabel>
            <TradeInfoBottomValue>
              {requiredMarginWithFee !== undefined
                ? currency(requiredMarginWithFee, {
                    precision: contractPriceStep.price_precision,
                  }).format()
                : "N/A"}
            </TradeInfoBottomValue>
          </TradeInfoBottomRow>
          <TradeInfoBottomRow>
            <TradeInfoBottomLabel $underline>Fees</TradeInfoBottomLabel>
            <TradeInfoBottomValue>
              {/* Null fees (no verified schedule) are "-", never "0.0000%". */}
              {formatFeeRates(feeStructure)}
            </TradeInfoBottomValue>
          </TradeInfoBottomRow>
        </TradeInfoBottomSection>
      )}

      {/* ── Deposit button ── */}
      {isPerp && (
        <DepositBtn type="button" onClick={() => openDepositFlow()}>
          Deposit
        </DepositBtn>
      )}

      {/* ── Withdraw ── */}
      {isPerp && (
        <ActionBtnsRow>
          <OutlineActionBtn type="button" onClick={() => setShowWithdrawModal(true)}>Withdraw</OutlineActionBtn>
        </ActionBtnsRow>
      )}

      {/* ── Close position ── */}
      {isPerp && currentPosition &&
        (replaceClosePositionButton || (
          <ClosePositionButton
            data-trade-close
            type="button"
            onClick={() => setClosePositionModal(true)}
            style={{ marginTop: SPACING.two }}
          >
            {!isLoading ? (
              commonFormTranslations("close_position")
            ) : (
              <Spinner color={TEXT_COLORS.two} />
            )}
          </ClosePositionButton>
        ))}

      <TradeExecutedBar
        type={orderDirection}
        animate={animControls}
        initial={{ opacity: 0, scale: 0 }}
      />
    </SubmitWrapper>
  );
}
